import React, { useEffect, useState, useRef } from 'react';
import { X, Menu, ChevronDownIcon, BuildingIcon, HomeIcon, SparklesIcon, TruckIcon, HammerIcon, KeyIcon, ArrowRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
const LOGO_WHITE = 'https://cdn.magicpatterns.com/uploads/jc63Wcs2z1JuqZfJSF71Nx/logo-white.png';
export function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isBookDropdownOpen, setIsBookDropdownOpen] = useState(false);
  const [isServicesDropdownOpen, setIsServicesDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const servicesDropdownRef = useRef<HTMLDivElement>(null);
  const dropdownTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const servicesTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const location = useLocation();
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsBookDropdownOpen(false);
    setIsServicesDropdownOpen(false);
  }, [location]);
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsBookDropdownOpen(false);
      }
      if (servicesDropdownRef.current && !servicesDropdownRef.current.contains(e.target as Node)) {
        setIsServicesDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const handleDropdownEnter = () => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
      dropdownTimeoutRef.current = null;
    }
    setIsBookDropdownOpen(true);
  };
  const handleDropdownLeave = () => {
    dropdownTimeoutRef.current = setTimeout(() => {
      setIsBookDropdownOpen(false);
    }, 150);
  };
  const handleServicesEnter = () => {
    if (servicesTimeoutRef.current) {
      clearTimeout(servicesTimeoutRef.current);
      servicesTimeoutRef.current = null;
    }
    setIsServicesDropdownOpen(true);
  };
  const handleServicesLeave = () => {
    servicesTimeoutRef.current = setTimeout(() => {
      setIsServicesDropdownOpen(false);
    }, 150);
  };
  const navLinks = [{
    name: 'Home',
    href: '/'
  }, {
    name: 'Our services',
    href: '/services'
  }, {
    name: 'About us',
    href: '/about'
  }, {
    name: 'FAQ',
    href: '/faq'
  }, {
    name: 'Contact us',
    href: '/contact'
  }];
  const serviceLinks = [{
    name: 'Commercial Cleaning',
    href: '/book/commercial',
    icon: 'BuildingIcon',
    image: 'https://cdn.magicpatterns.com/uploads/rwKue5f9hgNLxaBHg67fDd/commercial-cleaning.webp'
  }, {
    name: 'Basic Home Cleaning',
    href: '/book/home-cleaning',
    icon: 'HomeIcon',
    image: 'https://cdn.magicpatterns.com/uploads/w97pAeAsHvXpsZjJXJv9zy/bathroom-2.jpg'
  }, {
    name: 'Spring / Deep Cleaning',
    href: '/book/deep-cleaning',
    icon: 'SparklesIcon',
    image: 'https://cdn.magicpatterns.com/uploads/qFEAhAzNFBMWi5a4av2PfT/bathroom-victorian.jpg'
  }, {
    name: 'Move-In / Move-Out',
    href: '/book/move-in-out',
    icon: 'TruckIcon',
    image: 'https://cdn.magicpatterns.com/uploads/fdByeNhBpS4Fggi9bFdohH/move-in-move-out.jpg'
  }, {
    name: 'Post Renovation',
    href: '/book/post-renovation',
    icon: 'HammerIcon',
    image: 'https://cdn.magicpatterns.com/uploads/dXnDoZhpzXSRg5x1KuQAzx/Construction-Cleaning.jpg'
  }, {
    name: 'Airbnb / Short Term Rental',
    href: '/book/airbnb',
    icon: 'KeyIcon',
    image: 'https://cdn.magicpatterns.com/uploads/ok9mJDbncPyAoSjqCm1Wq7/home-clean.png'
  }];
  return <>
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled || location.pathname !== '/' ? 'bg-primary/95 backdrop-blur-md shadow-lg py-3' : 'bg-transparent py-5'}`} data-id="element-156">
        <div className="container mx-auto px-6 md:px-12 flex items-center justify-between" data-id="element-157">
          {/* Logo */}
          <Link to="/" className="z-[70] relative" data-id="element-158">
            <img src={LOGO_WHITE} alt="Smith & Jengs" className="h-14 md:h-16 lg:h-18" data-id="element-159" />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-6 xl:gap-8" data-id="element-160">
            {navLinks.map(link => {
            const isActive = link.href === '/' ? location.pathname === '/' : location.pathname.startsWith(link.href.replace('/#', '/'));
            if (link.name === 'Our services') {
              return <div key={link.name} ref={servicesDropdownRef} className="relative" onMouseEnter={handleServicesEnter} onMouseLeave={handleServicesLeave} data-id="element-161">
                    <button onClick={() => setIsServicesDropdownOpen(!isServicesDropdownOpen)} className={`text-sm font-medium tracking-wide transition-colors inline-flex items-center gap-1 ${isActive || isServicesDropdownOpen ? 'text-accent' : 'text-white/80 hover:text-white'}`} data-id="element-162">
                      {link.name}
                      <ChevronDownIcon size={14} className={`transition-transform duration-200 ${isServicesDropdownOpen ? 'rotate-180' : ''}`} data-id="element-163" />
                    </button>

                    {/* Invisible bridge to prevent hover gap */}
                    {isServicesDropdownOpen && <div className="fixed left-0 right-0 top-[50px] h-[25px] z-[79]" onMouseEnter={handleServicesEnter} data-id="element-164" />}

                    <AnimatePresence data-id="element-165">
                      {isServicesDropdownOpen && <motion.div key="services-dropdown" initial={{
                    opacity: 0,
                    y: 8,
                    scale: 0.98
                  }} animate={{
                    opacity: 1,
                    y: 0,
                    scale: 1
                  }} exit={{
                    opacity: 0,
                    y: 8,
                    scale: 0.98
                  }} transition={{
                    duration: 0.2,
                    ease: 'easeOut'
                  }} className="fixed left-4 right-4 top-[70px] max-w-[700px] mx-auto bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden overflow-y-auto max-h-[calc(100vh-90px)] z-[80]" onMouseEnter={handleServicesEnter} onMouseLeave={handleServicesLeave} data-id="element-166">
                          <div className="p-4 md:p-6 lg:p-8" data-id="element-167">
                            <div className="flex items-center gap-2 mb-4 md:mb-6" data-id="element-168">
                              <h3 className="text-lg md:text-xl font-heading font-bold text-primary" data-id="element-169">
                                Discover our cleaning services
                              </h3>
                              <span className="text-accent font-heading font-bold text-lg md:text-xl" data-id="element-170">
                                at Smith & Jengs
                              </span>
                            </div>

                            <div className="grid grid-cols-2 xl:grid-cols-3 gap-3 lg:gap-4" data-id="element-171">
                              {serviceLinks.map(service => {
                          const IconMap: Record<string, React.ElementType> = {
                            BuildingIcon,
                            HomeIcon,
                            SparklesIcon,
                            TruckIcon,
                            HammerIcon,
                            KeyIcon
                          };
                          const Icon = IconMap[service.icon];
                          return <Link key={service.name} to={service.href} className="group relative h-24 md:h-28 lg:h-32 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all" data-id="element-172">
                                    <img src={service.image} alt={service.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" data-id="element-173" />
                                    <div className="absolute inset-0 bg-gradient-to-t from-primary/90 via-primary/40 to-transparent" data-id="element-174"></div>

                                    <div className="absolute bottom-2 left-3 right-3 md:bottom-3 md:left-4 md:right-4" data-id="element-175">
                                      <p className="text-white font-medium text-xs md:text-sm leading-tight group-hover:text-accent transition-colors" data-id="element-176">
                                        {service.name}
                                      </p>
                                    </div>
                                  </Link>;
                        })}

                              {/* CTA Card */}
                              <Link to="/services" className="col-span-2 xl:col-span-3 group relative h-20 lg:h-24 rounded-xl overflow-hidden bg-primary shadow-sm hover:shadow-md transition-all flex flex-col justify-center p-4 lg:p-6 border border-primary-light" data-id="element-177">
                                <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-accent/20 via-primary to-primary opacity-50" data-id="element-178"></div>
                                <div className="relative z-10 flex items-center justify-between" data-id="element-179">
                                  <div data-id="element-180">
                                    <h4 className="text-white font-heading font-bold text-xl mb-1 group-hover:text-accent transition-colors" data-id="element-181">
                                      View all services
                                    </h4>
                                    <p className="text-white/60 text-xs" data-id="element-182">
                                      Explore our full range of cleaning
                                      solutions
                                    </p>
                                  </div>
                                  <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center group-hover:bg-accent transition-colors" data-id="element-183">
                                    <ArrowRight size={18} className="text-primary group-hover:text-white transition-colors" data-id="element-184" />
                                  </div>
                                </div>
                              </Link>
                            </div>
                          </div>
                        </motion.div>}
                    </AnimatePresence>
                  </div>;
            }
            return <Link key={link.name} to={link.href} className={`text-sm font-medium tracking-wide transition-colors ${isActive ? 'text-accent' : 'text-white/80 hover:text-white'}`} data-id="element-185">
                  {link.name}
                </Link>;
          })}
            {/* Book Now Dropdown */}
            <div ref={dropdownRef} className="relative" onMouseEnter={handleDropdownEnter} onMouseLeave={handleDropdownLeave} data-id="element-186">
              <button onClick={() => setIsBookDropdownOpen(!isBookDropdownOpen)} className="bg-accent hover:bg-accent/90 text-white px-6 py-2.5 rounded-lg text-sm font-bold tracking-wide transition-all transform hover:scale-105 inline-flex items-center gap-2" data-id="element-187">
                Book now
                <ChevronDownIcon size={16} className={`transition-transform duration-200 ${isBookDropdownOpen ? 'rotate-180' : ''}`} data-id="element-188" />
              </button>

              <AnimatePresence data-id="element-189">
                {isBookDropdownOpen && <motion.div initial={{
                opacity: 0,
                y: 8,
                scale: 0.95
              }} animate={{
                opacity: 1,
                y: 0,
                scale: 1
              }} exit={{
                opacity: 0,
                y: 8,
                scale: 0.95
              }} transition={{
                duration: 0.2,
                ease: 'easeOut'
              }} className="fixed right-4 top-[70px] w-72 bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden overflow-y-auto max-h-[calc(100vh-90px)] z-[80]" data-id="element-190">
                    <div className="p-2" data-id="element-191">
                      <p className="px-4 pt-3 pb-2 text-xs font-bold uppercase tracking-widest text-gray-400" data-id="element-192">
                        Choose a Service
                      </p>
                      {serviceLinks.map(link => {
                    const IconMap: Record<string, React.ElementType> = {
                      BuildingIcon,
                      HomeIcon,
                      SparklesIcon,
                      TruckIcon,
                      HammerIcon,
                      KeyIcon
                    };
                    const Icon = IconMap[link.icon];
                    return <Link key={link.name} to={link.href} className="flex items-center gap-3 px-4 py-3 rounded-lg text-primary hover:bg-accent/5 hover:text-accent transition-all group" data-id="element-193">
                            <div className="w-8 h-8 rounded-lg bg-accent/10 group-hover:bg-accent/20 flex items-center justify-center transition-colors" data-id="element-194">
                              {Icon && <Icon size={16} className="text-accent" data-id="element-195" />}
                            </div>
                            <span className="text-sm font-medium" data-id="element-196">
                              {link.name}
                            </span>
                          </Link>;
                  })}
                    </div>
                    <div className="border-t border-gray-100 p-3" data-id="element-197">
                      <Link to="/services" className="block text-center text-xs font-bold uppercase tracking-widest text-accent hover:text-primary transition-colors py-1" data-id="element-198">
                        View All Services →
                      </Link>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>
          </nav>

          {/* Mobile Menu Toggle */}
          <button className="lg:hidden z-[70] relative p-1" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'} data-id="element-199">
            {isMobileMenuOpen ? <X size={32} strokeWidth={2.5} className="text-accent" data-id="element-200" /> : <Menu size={32} className="text-accent" data-id="element-201" />}
          </button>
        </div>
      </header>

      {/* Backdrop overlay */}
      <AnimatePresence data-id="element-202">
        {isMobileMenuOpen && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 bg-black/50 z-[55] lg:hidden" onClick={() => setIsMobileMenuOpen(false)} data-id="element-203" />}
      </AnimatePresence>

      {/* Side Panel Menu */}
      <AnimatePresence data-id="element-204">
        {isMobileMenuOpen && <motion.div initial={{
        x: '100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '100%'
      }} transition={{
        type: 'spring',
        damping: 28,
        stiffness: 250
      }} className="fixed top-0 right-0 bottom-0 w-[280px] bg-primary z-[60] flex flex-col overflow-y-auto shadow-2xl lg:hidden" data-id="element-205">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-white/10" data-id="element-206">
              <span className="text-white font-heading font-bold text-lg tracking-wide" data-id="element-207">
                Menu
              </span>
              <button onClick={() => setIsMobileMenuOpen(false)} aria-label="Close menu" className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/10 transition-colors" data-id="element-208">
                <X size={20} className="text-white/70" data-id="element-209" />
              </button>
            </div>

            {/* Nav Links */}
            <nav className="flex flex-col px-6 py-4" data-id="element-210">
              {navLinks.map(link => <Link key={link.name} to={link.href} className={`py-3 text-[15px] font-medium border-b border-white/5 transition-colors ${location.pathname === link.href ? 'text-accent' : 'text-white/80 hover:text-accent'}`} data-id="element-211">
                  {link.name}
                </Link>)}
            </nav>

            {/* Services */}
            <div className="px-6 py-4 border-t border-white/10" data-id="element-212">
              <p className="text-white/30 text-[11px] font-bold uppercase tracking-widest mb-3" data-id="element-213">
                Services
              </p>
              <nav className="flex flex-col gap-0.5" data-id="element-214">
                {serviceLinks.map(link => {
              const IconMap: Record<string, React.ElementType> = {
                BuildingIcon,
                HomeIcon,
                SparklesIcon,
                TruckIcon,
                HammerIcon,
                KeyIcon
              };
              const Icon = IconMap[link.icon];
              return <Link key={link.name} to={link.href} className="flex items-center gap-3 text-white/70 hover:text-accent py-2 transition-colors" data-id="element-215">
                      {Icon && <Icon size={14} className="text-accent/50 shrink-0" data-id="element-216" />}
                      <span className="text-[13px]" data-id="element-217">{link.name}</span>
                    </Link>;
            })}
              </nav>
            </div>

            {/* Book Now CTA */}
            <div className="mt-auto px-6 py-6 border-t border-white/10" data-id="element-218">
              <Link to="/services" className="block bg-accent hover:bg-accent/85 text-white font-bold tracking-wide text-center py-3.5 rounded-lg transition-all text-sm" data-id="element-219">
                Book now
              </Link>
            </div>
          </motion.div>}
      </AnimatePresence>
    </>;
}